import React from "react";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {
  Boxes,
  ExternalLink,
  FileImage,
  FileSpreadsheet,
  FileText,
  Globe,
  Headphones,
  Play,
  Presentation,
} from "lucide-react";

import type {
  ContentProgress,
  ContentProgressItem,
  ContentProgressSectionKind,
} from "../../@types/portalAluno";
import {
  calculateProgressPercentage,
  getCompletedItemIds,
} from "../../utils/portalAluno/contentProgress";
import FeedbackOverlay, {
  FeedbackValue,
} from "../FeedbackOverlay/FeedbackOverlay";
import "./styles.css";

interface ContentProgressPanelProps {
  completedItemIds?: ReadonlySet<string>;
  progress: ContentProgress;
  feedbackSubjectLabel?: string;
  initialFeedbackComment?: string;
  initialRating?: number;
  onFeedbackSubmit?: (feedback: FeedbackValue) => void | Promise<void>;
  onItemSelect?: (
    item: ContentProgressItem,
    sectionKind: ContentProgressSectionKind
  ) => boolean | void | Promise<boolean | void>;
  onStatusChange?: (message: string) => void;
  selectedItemId?: string;
  showProgress?: boolean;
}

const itemTypeLabels: Record<ContentProgressItem["contentType"], string> = {
  audio: "Áudio",
  document: "Documento",
  download: "Arquivo",
  evaluation: "Avaliação",
  external: "Link externo",
  html: "Página HTML",
  image: "Imagem",
  internal: "Conteúdo interno",
  package: "Pacote",
  pdf: "PDF",
  presentation: "Apresentação",
  quiz: "Quiz",
  scorm: "SCORM",
  spreadsheet: "Planilha",
  video: "Vídeo",
};

const audioFilePattern = /\.(?:aac|flac|m4a|mp3|oga|ogg|wav)(?:[?#].*)?$/i;
const videoFilePattern = /\.(?:3gp|3gpp|avi|m3u8|m4v|mkv|mov|mp4|webm)(?:[?#].*)?$/i;
const pdfFilePattern = /\.pdf(?:[?#].*)?$/i;

const getEffectiveContentType = (
  item: ContentProgressItem
): ContentProgressItem["contentType"] => {
  if (item.externalUrl) return "external";

  const sourceUrl = item.sourceUrl ?? "";

  if (audioFilePattern.test(sourceUrl)) return "audio";
  if (videoFilePattern.test(sourceUrl)) return "video";
  if (pdfFilePattern.test(sourceUrl)) return "pdf";
  return item.contentType;
};

const getItemIcon = (contentType: ContentProgressItem["contentType"]) => {
  switch (contentType) {
    case "audio":
      return <Headphones aria-hidden="true" strokeWidth={2} />;
    case "pdf":
      return <FileText aria-hidden="true" strokeWidth={2} />;
    case "scorm":
      return <Boxes aria-hidden="true" strokeWidth={2} />;
    case "document":
      return <FileText aria-hidden="true" strokeWidth={2} />;
    case "spreadsheet":
      return <FileSpreadsheet aria-hidden="true" strokeWidth={2} />;
    case "presentation":
      return <Presentation aria-hidden="true" strokeWidth={2} />;
    case "image":
      return <FileImage aria-hidden="true" strokeWidth={2} />;
    case "html":
      return <Globe aria-hidden="true" strokeWidth={2} />;
    case "internal":
      return <Play aria-hidden="true" fill="currentColor" strokeWidth={2} />;
    case "package":
      return <Boxes aria-hidden="true" strokeWidth={2} />;
    case "external":
      return <ExternalLink aria-hidden="true" strokeWidth={2} />;
    case "download":
      return <FileText aria-hidden="true" strokeWidth={2} />;
    case "quiz":
      return <QuizOutlinedIcon aria-hidden="true" />;
    case "evaluation":
      return <FactCheckOutlinedIcon aria-hidden="true" />;
    default:
      return <Play aria-hidden="true" fill="currentColor" strokeWidth={2} />;
  }
};

const ContentProgressPanel = ({
  completedItemIds,
  progress,
  feedbackSubjectLabel,
  initialFeedbackComment = "",
  initialRating = 0,
  onFeedbackSubmit,
  onItemSelect,
  onStatusChange,
  selectedItemId,
  showProgress = true,
}: ContentProgressPanelProps) => {
  const initiallyCompletedIds = React.useMemo(
    () => getCompletedItemIds(progress.sections),
    [progress]
  );
  const [expandedSectionId, setExpandedSectionId] = React.useState<
    string | null
  >(
    progress.sections[0]?.id ?? null
  );
  const [activeItemId, setActiveItemId] = React.useState("");
  const hasCourseSections = progress.sections.some(
    (section) => section.kind === "course"
  );
  const [rating, setRating] = React.useState(initialRating);
  const [hoveredRating, setHoveredRating] = React.useState(0);
  const [pendingRating, setPendingRating] = React.useState(initialRating);
  const [feedbackComment, setFeedbackComment] = React.useState(
    initialFeedbackComment
  );
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const effectiveCompletedIds = React.useMemo(() => {
    const mergedIds = new Set(initiallyCompletedIds);
    completedItemIds?.forEach((id) => mergedIds.add(id));
    return mergedIds;
  }, [completedItemIds, initiallyCompletedIds]);

  React.useEffect(() => {
    if (!selectedItemId) return;

    const selectedSection = progress.sections.find((section) =>
      section.items.some((item) => item.id === selectedItemId)
    );
    if (selectedSection) setExpandedSectionId(selectedSection.id);
  }, [progress.sections, selectedItemId]);

  const progressPercentage = Math.floor(
    calculateProgressPercentage(progress, effectiveCompletedIds)
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSectionId((current) =>
      current === sectionId ? null : sectionId
    );
  };

  const handleItemClick = async (
    sectionId: string,
    sectionTitle: string,
    sectionKind: ContentProgressSectionKind,
    item: ContentProgressItem
  ) => {
    const shouldSelect = await onItemSelect?.(item, sectionKind);
    if (shouldSelect === false) return;

    setExpandedSectionId(sectionId);
    setActiveItemId(item.id);

    if (sectionKind === "evaluation") {
      onStatusChange?.(`${item.title} aberta.`);
      return;
    }

    if (getEffectiveContentType(item) === "pdf" && !item.pdf?.sourceUrl) {
      onStatusChange?.("PDF não disponível no momento.");
      return;
    }

    if (effectiveCompletedIds.has(item.id)) {
      onStatusChange?.(`${item.title} selecionado.`);
      return;
    }

    const effectiveContentType = getEffectiveContentType(item);
    if (effectiveContentType === "external") {
      onStatusChange?.(
        `${item.title} será concluído após abrir o conteúdo externo.`
      );
      return;
    }
    if (effectiveContentType === "scorm") {
      onStatusChange?.(`${item.title} será concluído ao finalizar o SCORM.`);
      return;
    }

    onStatusChange?.(`${item.title} selecionado em ${sectionTitle}.`);
  };

  const openCurrentSection = () => {
    const effectiveActiveItemId = selectedItemId ?? activeItemId;
    const currentSection = progress.sections.find((section) =>
      section.items.some((item) => item.id === effectiveActiveItemId)
    );

    setExpandedSectionId(currentSection?.id ?? progress.sections[0]?.id ?? null);
    setIsMenuOpen(false);
    onStatusChange?.("Etapa atual aberta.");
  };

  const collapseAllSections = () => {
    setExpandedSectionId(null);
    setIsMenuOpen(false);
    onStatusChange?.("Todas as etapas foram recolhidas.");
  };

  const closeFeedback = React.useCallback(() => {
    setIsFeedbackOpen(false);
    setPendingRating(rating);
  }, [rating]);

  const handleFeedbackSubmit = async (feedback: FeedbackValue) => {
    try {
      await onFeedbackSubmit?.(feedback);
      setRating(feedback.rating);
      setPendingRating(feedback.rating);
      setFeedbackComment(feedback.comment);
      setIsFeedbackOpen(false);
      onStatusChange?.(
        `Feedback enviado com ${feedback.rating} ${
          feedback.rating === 1 ? "estrela" : "estrelas"
        }.`
      );
    } catch {
      onStatusChange?.("Não foi possível enviar o feedback. Tente novamente.");
    }
  };

  return (
    <>
      <aside
        className={`content-progress-panel${
          showProgress ? "" : " content-progress-panel--without-progress"
        }`}
        aria-label={
          showProgress ? undefined : "Conteúdos relacionados e feedback"
        }
        aria-labelledby={showProgress ? "content-progress-title" : undefined}
        onKeyDown={(event) => {
          if (event.key === "Escape") setIsMenuOpen(false);
        }}
      >
      {showProgress && (
        <>
          <div className="content-progress-panel__heading">
            <h2 id="content-progress-title">Progresso</h2>
            <div className="content-progress-panel__menu-wrapper">
              <button
                type="button"
                className="content-progress-panel__more"
                onClick={() => setIsMenuOpen((current) => !current)}
                aria-label="Opções do progresso"
                aria-expanded={isMenuOpen}
                aria-controls="content-progress-menu"
              >
                <MoreHorizRoundedIcon aria-hidden="true" />
              </button>
              {isMenuOpen && (
                <div
                  id="content-progress-menu"
                  className="content-progress-panel__menu"
                  role="menu"
                >
                  <button type="button" role="menuitem" onClick={openCurrentSection}>
                    Abrir etapa atual
                  </button>
                  <button type="button" role="menuitem" onClick={collapseAllSections}>
                    Recolher todas
                  </button>
                </div>
              )}
            </div>
          </div>

          <div
            className="content-progress-panel__bar"
            role="progressbar"
            aria-label="Progresso do treinamento"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercentage}
            aria-valuetext={`${progressPercentage}% concluído`}
          >
            <span style={{ width: `${progressPercentage}%` }} />
          </div>

          <div className="content-progress-panel__summary">
            <strong>{progress.title}</strong>
            <span>{progressPercentage}%</span>
          </div>
        </>
      )}

      {hasCourseSections && (
        <h3 className="content-progress-panel__list-title">
          {progress.listTitle}
        </h3>
      )}

      <div className="content-progress-panel__sections">
        {progress.sections.map((section) => {
          const isExpanded = expandedSectionId === section.id;
          const newlyCompletedInSection = section.items.filter(
            (item) =>
              effectiveCompletedIds.has(item.id) &&
              !initiallyCompletedIds.has(item.id)
          ).length;
          const completedInSection = Math.min(
            section.totalItems,
            section.completedItems + newlyCompletedInSection
          );
          const panelId = `progress-section-${section.id}`;

          return (
            <section className="content-progress-section" key={section.id}>
              <button
                type="button"
                className="content-progress-section__toggle"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isExpanded}
                aria-controls={panelId}
              >
                <span>{section.title}</span>
                {showProgress && (
                  <span className="content-progress-section__count">
                    ({completedInSection}/{section.totalItems})
                  </span>
                )}
                <span className="content-progress-section__chevron" aria-hidden="true">
                  {isExpanded ? (
                    <ExpandLessRoundedIcon />
                  ) : (
                    <ExpandMoreRoundedIcon />
                  )}
                </span>
              </button>

              {isExpanded && (
                <div
                  id={panelId}
                  className="content-progress-section__items"
                >
                  {section.items.map((item) => {
                    const isCompleted = effectiveCompletedIds.has(item.id);
                    const effectiveContentType = getEffectiveContentType(item);
                    const isActive =
                      selectedItemId !== undefined
                        ? selectedItemId === item.id
                        : activeItemId === item.id;

                    return (
                      <button
                        type="button"
                        key={item.id}
                        className={`content-progress-item${
                          isActive ? " content-progress-item--active" : ""
                        }`}
                        onClick={() =>
                          handleItemClick(
                            section.id,
                            section.title,
                            section.kind,
                            item
                          )
                        }
                        disabled={isActive}
                        aria-current={isActive ? "true" : undefined}
                        aria-pressed={isActive}
                      >
                        <span
                          className="content-progress-item__icon"
                          data-content-type={effectiveContentType}
                          aria-label={`Tipo: ${itemTypeLabels[effectiveContentType]}`}
                          title={itemTypeLabels[effectiveContentType]}
                        >
                          {getItemIcon(effectiveContentType)}
                        </span>
                        <span className="content-progress-item__title">
                          {item.title}
                        </span>
                        {showProgress && (
                          <span
                            className={`content-progress-item__check${
                              isCompleted ? " is-completed" : ""
                            }`}
                            aria-label={
                              isCompleted ? "Concluído" : "Não concluído"
                            }
                          >
                            <CheckCircleRoundedIcon aria-hidden="true" />
                          </span>
                        )}
                        <span className="content-progress-item__duration">
                          {item.duration}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="content-progress-rating">
        <strong>FEEDBACK:</strong>
        <div
          className="content-progress-rating__stars"
          onMouseLeave={() => setHoveredRating(0)}
          aria-label={
            showProgress ? "Feedback do treinamento" : "Feedback do conteúdo"
          }
        >
          {Array.from({ length: 5 }, (_, index) => {
            const value = index + 1;
            const isHighlighted = value <= (hoveredRating || rating);

            return (
              <button
                type="button"
                key={value}
                className={isHighlighted ? "is-selected" : ""}
                onMouseEnter={() => setHoveredRating(value)}
                onFocus={() => setHoveredRating(value)}
                onBlur={() => setHoveredRating(0)}
                onClick={() => {
                  setPendingRating(value);
                  setHoveredRating(0);
                  setIsFeedbackOpen(true);
                }}
                aria-label={`Dar feedback com ${value} ${
                  value === 1 ? "estrela" : "estrelas"
                }`}
                aria-pressed={rating === value}
              >
                <StarRoundedIcon aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </div>
      </aside>

      <FeedbackOverlay
        open={isFeedbackOpen}
        targetTitle={progress.title}
        subjectLabel={feedbackSubjectLabel}
        initialRating={pendingRating || rating}
        initialComment={feedbackComment}
        hasFeedback={rating > 0}
        onClose={closeFeedback}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );
};

export default ContentProgressPanel;
